import React, { useState, useEffect, useRef, useCallback } from 'react';
import config from '../../config/config';

const API_BASE = config.api.baseUrl;

/**
 * Internal Chat Component - WhatsApp-style messaging
 * Features: Groups, File Sharing, Voice Messages, Online Status,
 * Tab Badge, Desktop Notifications, Drag & Drop, Message Actions
 */

const InternalChat = ({ currentUser, externalOpen, onOpenChange, hideFloatingButton = false }) => {

  const [internalOpen, setInternalOpen] = useState(false);

  // Support both internal and external control
  const isOpen = externalOpen !== undefined ? externalOpen : internalOpen;
  const setIsOpen = (value) => {
    if (onOpenChange) {
      onOpenChange(value);
    } else {
      setInternalOpen(value);
    }
  };
  const [view, setView] = useState('conversations'); // conversations, chat, new-chat, new-group, group-info, profile-settings
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [newMessagePopup, setNewMessagePopup] = useState(null);
  const [selectedGroupMembers, setSelectedGroupMembers] = useState([]);
  const [groupName, setGroupName] = useState('');
  const [groupDescription, setGroupDescription] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [showAttachMenu, setShowAttachMenu] = useState(false);
  const [uploadingProfilePic, setUploadingProfilePic] = useState(false);

  // New states for enhanced features
  const [isDragging, setIsDragging] = useState(false);
  const [messageMenuOpen, setMessageMenuOpen] = useState(null); // message id
  const [editingMessage, setEditingMessage] = useState(null);
  const [editText, setEditText] = useState('');
  const [replyingTo, setReplyingTo] = useState(null);
  const [notificationPermission, setNotificationPermission] = useState('default');
  const [originalTitle] = useState(document.title);

  // Screen sharing states
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [screenShareStream, setScreenShareStream] = useState(null);
  const [incomingScreenShareRequest, setIncomingScreenShareRequest] = useState(null);
  const [activeScreenShare, setActiveScreenShare] = useState(null); // { from, streamData }
  const [isScreenShareFullscreen, setIsScreenShareFullscreen] = useState(false);
  const [screenShareFrame, setScreenShareFrame] = useState(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [pendingScreenShareTo, setPendingScreenShareTo] = useState(null);

  // Remote control states
  const [remoteControlActive, setRemoteControlActive] = useState(false); // Am I being controlled?
  const [isControllingRemote, setIsControllingRemote] = useState(false); // Am I controlling someone?
  const [incomingControlRequest, setIncomingControlRequest] = useState(null); // Request from viewer
  const [pendingControlRequest, setPendingControlRequest] = useState(false); // Waiting for approval
  const [controlRequestFrom, setControlRequestFrom] = useState(null); // Who requested control

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const profilePicInputRef = useRef(null);
  const pollIntervalRef = useRef(null);
  const lastCheckRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingIntervalRef = useRef(null);
  const shownPopupsRef = useRef(new Set()); // Track shown popup message IDs
  const chatContainerRef = useRef(null);
  const dragInputRef = useRef(null);
  const screenShareVideoRef = useRef(null);
  const screenShareCanvasRef = useRef(null);
  const screenShareIntervalRef = useRef(null);
  const screenShareViewerRef = useRef(null);

  // Get session token
  const getToken = () => {
    // Try regular session token first
    let token = localStorage.getItem('session_token') || sessionStorage.getItem('session_token');
    if (token) return token;

    // Try technician session token
    token = sessionStorage.getItem('tech_session_token') || localStorage.getItem('tech_session_token');
    if (token) return token;

    // Try legacy afikim_session format
    try {
      const session = JSON.parse(localStorage.getItem('afikim_session') || '{}');
      if (session.token) return session.token;
    } catch (e) {}

    return '';
  };

  // API call helper
  const apiCall = useCallback(async (endpoint, options = {}) => {
    const token = getToken();
    const userEmail = currentUser?.email || currentUser?.username || '';
    try {
      const response = await fetch(`${API_BASE}${endpoint}`, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : '',
          'x-user-email': userEmail,
          ...options.headers
        }
      });

      // Check if response is JSON
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        console.warn(`API returned non-JSON response for ${endpoint}`);
        return { success: false, error: 'Server returned non-JSON response' };
      }

      return response.json();
    } catch (err) {
      console.error(`API call failed for ${endpoint}:`, err);
      return { success: false, error: err.message };
    }
  }, [currentUser?.email, currentUser?.username]);

  // Request notification permission
  const requestNotificationPermission = async () => {
    if ('Notification' in window) {
      const permission = await Notification.requestPermission();
      setNotificationPermission(permission);
    }
  };

  // Update browser tab title with unread count
  useEffect(() => {
    if (unreadCount > 0) {
      document.title = `(${unreadCount}) ${originalTitle}`;
    } else {
      document.title = originalTitle;
    }
  }, [unreadCount, originalTitle]);

  // Request notification permission on mount
  useEffect(() => {
    if ('Notification' in window) {
      setNotificationPermission(Notification.permission);
      if (Notification.permission === 'default') {
        requestNotificationPermission();
      }
    }
  }, []);


  // Load conversations
  const loadConversations = useCallback(async () => {
    try {
      const data = await apiCall('/api/chat/conversations');
      if (data.success) {
        setConversations(data.conversations || []);
      }
    } catch (err) {
      console.error('Error loading conversations:', err);
    }
  }, []);

  // Load unread count
  const loadUnreadCount = useCallback(async () => {
    try {
      const data = await apiCall('/api/chat/unread');
      if (data.success) {
        setUnreadCount(data.unread_count || 0);
      }
    } catch (err) {
      console.error('Error loading unread count:', err);
    }
  }, []);

  // Update online status
  const updateOnlineStatus = useCallback(async () => {
    try {
      await apiCall('/api/chat/status', {
        method: 'POST',
        body: JSON.stringify({ status: 'online' })
      });
    } catch (err) {
      console.error('Error updating status:', err);
    }
  }, []);


  // Check for new messages
  const checkNewMessages = useCallback(async () => {
    try {
      const since = lastCheckRef.current;
      const data = await apiCall(`/api/chat/new${since ? `?since=${since}` : ''}`);
      if (data.success && data.messages && data.messages.length > 0) {
        // Always update conversations list and unread count
        loadConversations();
        loadUnreadCount();

        // Process each new message for notifications
        for (const message of data.messages) {
          const messageId = message.id || `${message.sender}_${message.timestamp}`;

          // Skip if already shown notification for this message
          if (shownPopupsRef.current.has(messageId)) {
            continue;
          }

          // Don't show popup if chat is open with this conversation
          const isCurrentConversation = isOpen && selectedConversation && (
            (selectedConversation.type === 'private' && selectedConversation.other_user === message.sender) ||
            (selectedConversation.type === 'group' && selectedConversation.id === message.group_id)
          );

          // If this is the current open conversation, just load messages
          if (isCurrentConversation) {
            loadMessages();
            shownPopupsRef.current.add(messageId);
            continue;
          }

          // Mark as shown BEFORE showing notification to prevent duplicates
          shownPopupsRef.current.add(messageId);

          // Keep only last 100 message IDs to prevent memory issues
          if (shownPopupsRef.current.size > 100) {
            const arr = Array.from(shownPopupsRef.current);
            shownPopupsRef.current = new Set(arr.slice(-50));
          }

          const displayName = message.sender_name || message.sender?.split('@')[0] || message.sender;

          // Show desktop notification when tab is inactive (only once per message)
          if (document.hidden && notificationPermission === 'granted') {
            const notifTitle = message.conversation_type === 'group'
              ? message.group_name
              : displayName;
            const notifBody = message.conversation_type === 'group'
              ? `${displayName}: ${message.content}`
              : message.content;

            const notification = new Notification(notifTitle, {
              body: notifBody,
              icon: '/assets/afikim_connect_logo.png',
              badge: '/assets/afikim_connect_logo.png',
              tag: `afikim-chat-${messageId}`, // Unique tag per message
              requireInteraction: false,
              silent: false
            });

            notification.onclick = () => {
              window.focus();
              setIsOpen(true);
              setSelectedConversation(message.conversation_type === 'group' ?
                { type: 'group', id: message.group_id, name: message.group_name } :
                { type: 'private', other_user: message.sender }
              );
              setView('chat');
              notification.close();
            };

            setTimeout(() => notification.close(), 5000);
          }

          // Show in-app popup for the latest message only
          setNewMessagePopup({
            sender: displayName,
            content: message.content,
            timestamp: message.timestamp,
            type: message.conversation_type,
            group_name: message.group_name,
            conversation: message.conversation_type === 'group' ?
              { type: 'group', id: message.group_id, name: message.group_name } :
              { type: 'private', other_user: message.sender }
          });

          // Auto-hide popup after 5 seconds
          setTimeout(() => setNewMessagePopup(null), 5000);
        }
      }
      lastCheckRef.current = new Date().toISOString();
    } catch (err) {
      console.error('Error checking new messages:', err);
    }
  }, [isOpen, selectedConversation, loadConversations, loadUnreadCount, notificationPermission]);

  // Load messages for current conversation
  const loadMessages = async () => {
    if (!selectedConversation) return;
    setLoading(true);
    try {
      let data;
      if (selectedConversation.type === 'group') {
        data = await apiCall(`/api/chat/groups/${selectedConversation.id}/messages`);
      } else {
        data = await apiCall(`/api/chat/messages/${encodeURIComponent(selectedConversation.other_user)}`);
      }
      if (data.success) {
        setMessages(data.messages || []);
      }
    } catch (err) {
      console.error('Error loading messages:', err);
    }
    setLoading(false);
  };

  // Load users for new chat
  const loadUsers = useCallback(async (query = '') => {
    try {
      const url = `/api/chat/users${query ? `?q=${encodeURIComponent(query)}` : ''}`;
      console.log('[Chat] Loading users with query:', query, 'URL:', url);
      const data = await apiCall(url);
      console.log('[Chat] Received users:', data?.users?.length || 0);
      if (data.success) {
        setUsers(data.users || []);
      }
    } catch (err) {
      console.error('Error loading users:', err);
    }
  }, [apiCall]);

  // Send message
  const sendMessage = async (messageType = 'text', attachmentData = null, voiceData = null) => {
    if (!selectedConversation) return;
    if (messageType === 'text' && !newMessage.trim()) return;

    const content = messageType === 'text' ? newMessage.trim() : '';
    if (messageType === 'text') setNewMessage('');

    try {
      let data;
      const messagePayload = {
        content,
        type: messageType,
        attachment: attachmentData,
        voice: voiceData,
        sender_name: currentUser?.name || currentUser?.username,
        reply_to: replyingTo?.id || null
      };

      if (selectedConversation.type === 'group') {
        data = await apiCall(`/api/chat/groups/${selectedConversation.id}/messages`, {
          method: 'POST',
          body: JSON.stringify(messagePayload)
        });
      } else {
        data = await apiCall('/api/chat/send', {
          method: 'POST',
          body: JSON.stringify({
            recipient: selectedConversation.other_user,
            ...messagePayload
          })
        });
      }

      if (data.success) {
        setMessages(prev => [...prev, data.message]);
        loadConversations();
        setReplyingTo(null);
      }
    } catch (err) {
      console.error('Error sending message:', err);
      if (messageType === 'text') setNewMessage(content);
    }
  };

  // Edit message
  const editMessage = async (messageId, newContent) => {
    try {
      const data = await apiCall(`/api/chat/messages/${messageId}`, {
        method: 'PUT',
        body: JSON.stringify({ content: newContent })
      });

      if (data.success) {
        setMessages(prev => prev.map(msg =>
          msg.id === messageId ? { ...msg, content: newContent, edited: true } : msg
        ));
        setEditingMessage(null);
        setEditText('');
      }
    } catch (err) {
      console.error('Error editing message:', err);
      alert('שגיאה בעריכת ההודעה');
    }
  };

  // Delete message
  const deleteMessage = async (messageId) => {
    try {
      const data = await apiCall(`/api/chat/messages/${messageId}`, {
        method: 'DELETE'
      });

      if (data.success) {
        setMessages(prev => prev.map(msg =>
          msg.id === messageId ? { ...msg, content: 'ההודעה נמחקה', deleted: true } : msg
        ));
      }
    } catch (err) {
      console.error('Error deleting message:', err);
      alert('שגיאה במחיקת ההודעה');
    }
    setMessageMenuOpen(null);
  };

  // Copy message to clipboard
  const copyMessage = (content) => {
    navigator.clipboard.writeText(content);
    setMessageMenuOpen(null);
  };

  // Forward message (placeholder)
  const forwardMessage = (msg) => {
    alert('העברת הודעה - בקרוב!');
    setMessageMenuOpen(null);
  };

  // Star message (placeholder)
  const starMessage = (msg) => {
    alert('הודעה סומנה בכוכב!');
    setMessageMenuOpen(null);
  };

  // Create group
  const createGroup = async () => {
    if (!groupName.trim() || selectedGroupMembers.length === 0) return;

    try {
      const data = await apiCall('/api/chat/groups', {
        method: 'POST',
        body: JSON.stringify({
          name: groupName.trim(),
          description: groupDescription.trim(),
          members: selectedGroupMembers.map(u => u.username)
        })
      });

      if (data.success) {
        setView('conversations');
        setGroupName('');
        setGroupDescription('');
        setSelectedGroupMembers([]);
        loadConversations();

        // Open the new group
        setSelectedConversation({ ...data.group, type: 'group' });
        setView('chat');
      }
    } catch (err) {
      console.error('Error creating group:', err);
      alert('שגיאה ביצירת הקבוצה');
    }
  };

  // Handle profile picture upload
  const handleProfilePicUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check file type
    if (!file.type.startsWith('image/')) {
      alert('יש לבחור קובץ תמונה');
      return;
    }

    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('התמונה גדולה מדי. מקסימום 5MB');
      return;
    }

    setUploadingProfilePic(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const token = getToken();
      const response = await fetch(`${API_BASE}/api/chat/profile-picture`, {
        method: 'POST',
        headers: {
          'Authorization': token ? `Bearer ${token}` : ''
        },
        body: formData
      });

      const data = await response.json();
      if (data.success) {
        alert('תמונת הפרופיל עודכנה בהצלחה!');
        setView('conversations');
      } else {
        alert(data.detail || 'שגיאה בהעלאת התמונה');
      }
    } catch (err) {
      console.error('Error uploading profile picture:', err);
      alert('שגיאה בהעלאת התמונה');
    } finally {
      setUploadingProfilePic(false);
    }
  };

  // Handle file selection
  const handleFileSelect = async (e, isImage = false) => {
    const file = e.target.files?.[0];
    if (!file) return;

    await processFile(file, isImage);
    e.target.value = '';
    setShowAttachMenu(false);
  };

  // Process file for upload
  const processFile = async (file, isImage = false) => {
    // Check file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      alert('הקובץ גדול מדי. מקסימום 10MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = async () => {
      const attachmentData = {
        name: file.name,
        type: file.type,
        data: reader.result
      };

      // Determine if it's an image
      const fileIsImage = isImage || file.type.startsWith('image/');
      await sendMessage(fileIsImage ? 'image' : 'file', attachmentData);
    };
    reader.readAsDataURL(file);
  };

  // ========================================
  // Screen Sharing Functions
  // ========================================

  // Request screen share permission and start sharing
  // Check if screen sharing is supported
  const isScreenShareSupported = () => {
    return navigator.mediaDevices && 'getDisplayMedia' in navigator.mediaDevices;
  };

  // Check if running on mobile
  const isMobileDevice = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  };

  const startScreenShare = async () => {
    if (!selectedConversation || selectedConversation.type !== 'private') {
      alert('שיתוף מסך זמין רק בשיחות פרטיות');
      return;
    }

    // Check if screen sharing is supported
    if (!isScreenShareSupported()) {
      if (isMobileDevice()) {
        alert('שיתוף מסך אינו נתמך במכשירים ניידים. ניתן לצפות בשיתוף מסך של אחרים.');
      } else {
        alert('הדפדפן שלך אינו תומך בשיתוף מסך');
      }
      return;
    }

    try {
      // Request screen share permission
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: {
          cursor: 'always',
          displaySurface: 'monitor'
        },
        audio: false
      });

      setScreenShareStream(stream);
      setPendingScreenShareTo(selectedConversation.other_user);

      // Send screen share request to the other user
      await apiCall('/api/chat/screen-share/request', {
        method: 'POST',
        body: JSON.stringify({
          to_user: selectedConversation.other_user,
          from_name: currentUser?.name || currentUser?.username
        })
      });

      // Handle stream end (user clicks "Stop sharing")
      stream.getVideoTracks()[0].onended = () => {
        stopScreenShare();
      };

    } catch (err) {
      console.error('Error starting screen share:', err);
      if (err.name === 'NotAllowedError') {
        // User cancelled - do nothing
      } else if (err.name === 'NotSupportedError') {
        alert('שיתוף מסך אינו נתמך בדפדפן זה');
      } else {
        alert('שגיאה בהתחלת שיתוף המסך');
      }
    }
  };

  // Stop screen sharing
  const stopScreenShare = async () => {
    if (screenShareStream) {
      screenShareStream.getTracks().forEach(track => track.stop());
    }
    if (screenShareIntervalRef.current) {
      clearInterval(screenShareIntervalRef.current);
    }
    setScreenShareStream(null);
    setIsScreenSharing(false);
    setPendingScreenShareTo(null);

    // Notify the other user that screen share ended
    if (selectedConversation?.other_user) {
      try {
        await apiCall('/api/chat/screen-share/stop', {
          method: 'POST',
          body: JSON.stringify({
            to_user: selectedConversation.other_user
          })
        });
      } catch (err) {
        console.error('Error stopping screen share:', err);
      }
    }
  };

  // Accept incoming screen share request
  const acceptScreenShare = async () => {
    if (!incomingScreenShareRequest) return;

    try {
      await apiCall('/api/chat/screen-share/accept', {
        method: 'POST',
        body: JSON.stringify({
          from_user: incomingScreenShareRequest.from
        })
      });

      setActiveScreenShare({
        from: incomingScreenShareRequest.from,
        fromName: incomingScreenShareRequest.fromName
      });
      setIncomingScreenShareRequest(null);
    } catch (err) {
      console.error('Error accepting screen share:', err);
    }
  };

  // Reject incoming screen share request
  const rejectScreenShare = async () => {
    if (!incomingScreenShareRequest) return;

    try {
      await apiCall('/api/chat/screen-share/reject', {
        method: 'POST',
        body: JSON.stringify({
          from_user: incomingScreenShareRequest.from
        })
      });
    } catch (err) {
      console.error('Error rejecting screen share:', err);
    }
    setIncomingScreenShareRequest(null);
  };

  // Close screen share viewer
  const closeScreenShareViewer = () => {
    setActiveScreenShare(null);
    setIsScreenShareFullscreen(false);
    setScreenShareFrame(null);
  };

  // Toggle fullscreen for screen share viewer
  const toggleScreenShareFullscreen = () => {
    setIsScreenShareFullscreen(!isScreenShareFullscreen);
  };

  // ========================================
  // Remote Control Functions
  // ========================================

  // Request control of shared screen (viewer requests)
  const requestRemoteControl = async () => {
    if (!activeScreenShare) return;

    try {
      await apiCall('/api/chat/remote-control/request', {
        method: 'POST',
        body: JSON.stringify({
          to_user: activeScreenShare.from,
          from_name: currentUser?.name || currentUser?.username
        })
      });
      setPendingControlRequest(true);
    } catch (err) {
      console.error('Error requesting remote control:', err);
      alert('שגיאה בבקשת השליטה');
    }
  };

  // Accept remote control request (sharer accepts)
  const acceptRemoteControl = async () => {
    if (!incomingControlRequest) return;

    try {
      await apiCall('/api/chat/remote-control/accept', {
        method: 'POST',
        body: JSON.stringify({
          from_user: incomingControlRequest.from
        })
      });
      setRemoteControlActive(true);
      setControlRequestFrom(incomingControlRequest.from);
      setIncomingControlRequest(null);
    } catch (err) {
      console.error('Error accepting remote control:', err);
    }
  };

  // Reject remote control request
  const rejectRemoteControl = async () => {
    if (!incomingControlRequest) return;

    try {
      await apiCall('/api/chat/remote-control/reject', {
        method: 'POST',
        body: JSON.stringify({
          from_user: incomingControlRequest.from
        })
      });
    } catch (err) {
      console.error('Error rejecting remote control:', err);
    }
    setIncomingControlRequest(null);
  };

  // Stop remote control
  const stopRemoteControl = async () => {
    try {
      await apiCall('/api/chat/remote-control/stop', {
        method: 'POST',
        body: JSON.stringify({
          other_user: controlRequestFrom || activeScreenShare?.from
        })
      });
    } catch (err) {
      console.error('Error stopping remote control:', err);
    }
    setRemoteControlActive(false);
    setIsControllingRemote(false);
    setControlRequestFrom(null);
  };

  // Send mouse event to sharer
  const sendMouseEvent = async (eventType, x, y, button = 0) => {
    if (!isControllingRemote || !activeScreenShare) return;

    try {
      await apiCall('/api/chat/remote-control/mouse', {
        method: 'POST',
        body: JSON.stringify({
          to_user: activeScreenShare.from,
          event_type: eventType,
          x: x,
          y: y,
          button: button
        })
      });
    } catch (err) {
      // Silently fail for performance
    }
  };

  // Send keyboard event to sharer
  const sendKeyboardEvent = async (eventType, key, keyCode, modifiers = {}) => {
    if (!isControllingRemote || !activeScreenShare) return;

    try {
      await apiCall('/api/chat/remote-control/keyboard', {
        method: 'POST',
        body: JSON.stringify({
          to_user: activeScreenShare.from,
          event_type: eventType,
          key: key,
          key_code: keyCode,
          modifiers: modifiers
        })
      });
    } catch (err) {
      // Silently fail for performance
    }
  };

  // Handle mouse events on screen share viewer
  const handleViewerMouseEvent = (e, eventType) => {
    if (!isControllingRemote || !screenShareViewerRef.current) return;

    const rect = screenShareViewerRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;

    sendMouseEvent(eventType, x, y, e.button);
  };

  // Handle keyboard events when controlling
  const handleViewerKeyEvent = (e, eventType) => {
    if (!isControllingRemote) return;

    e.preventDefault();
    sendKeyboardEvent(eventType, e.key, e.keyCode, {
      ctrl: e.ctrlKey,
      alt: e.altKey,
      shift: e.shiftKey,
      meta: e.metaKey
    });
  };

  // Start broadcasting screen frames when accepted
  useEffect(() => {
    if (!screenShareStream || !pendingScreenShareTo) return;

    const video = document.createElement('video');
    video.srcObject = screenShareStream;
    video.play();

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    setIsScreenSharing(true);

    // Capture and send frames
    const sendFrame = async () => {
      if (!screenShareStream || !screenShareStream.active) {
        stopScreenShare();
        return;
      }

      // Scale down for better performance - max 1280x720
      const maxWidth = 1280;
      const maxHeight = 720;
      const videoWidth = video.videoWidth || 1920;
      const videoHeight = video.videoHeight || 1080;

      let width = videoWidth;
      let height = videoHeight;

      if (width > maxWidth) {
        height = Math.round(height * (maxWidth / width));
        width = maxWidth;
      }
      if (height > maxHeight) {
        width = Math.round(width * (maxHeight / height));
        height = maxHeight;
      }

      canvas.width = width;
      canvas.height = height;
      ctx.drawImage(video, 0, 0, width, height);

      try {
        // Quality 0.5 for smaller payload (was 0.6)
        const frameData = canvas.toDataURL('image/jpeg', 0.5);
        await apiCall('/api/chat/screen-share/frame', {
          method: 'POST',
          body: JSON.stringify({
            to_user: pendingScreenShareTo,
            frame: frameData
          })
        });
      } catch (err) {
        console.error('Error sending frame:', err);
      }
    };

    // Send frames every 200ms (5 FPS) - optimized for performance
    // 5 FPS is enough for screen sharing, reduces server load by 50%
    screenShareIntervalRef.current = setInterval(sendFrame, 200);

    return () => {
      if (screenShareIntervalRef.current) {
        clearInterval(screenShareIntervalRef.current);
      }
      video.remove();
    };
  }, [screenShareStream, pendingScreenShareTo]);

  // Poll for screen share updates
  useEffect(() => {
    if (!currentUser?.email && !currentUser?.username) return;

    const checkScreenShareUpdates = async () => {
      try {
        const data = await apiCall('/api/chat/screen-share/check');
        if (data.success) {
          // Handle incoming request
          if (data.pending_request && !incomingScreenShareRequest) {
            setIncomingScreenShareRequest({
              from: data.pending_request.from,
              fromName: data.pending_request.from_name
            });
          }

          // Handle acceptance of our request
          if (data.accepted && pendingScreenShareTo) {
            // Already broadcasting, nothing to do
          }

          // Handle rejection
          if (data.rejected && pendingScreenShareTo) {
            alert('הבקשה לשיתוף מסך נדחתה');
            stopScreenShare();
          }

          // Handle incoming frame
          if (data.frame && activeScreenShare) {
            setScreenShareFrame(data.frame);
            setMousePosition(data.mouse_position || { x: 0, y: 0 });
          }

          // Handle stop signal
          if (data.stopped) {
            setActiveScreenShare(null);
            setScreenShareFrame(null);
            setIsScreenShareFullscreen(false);
            setIsControllingRemote(false);
          }

          // Handle remote control updates
          if (data.control_request && !incomingControlRequest && isScreenSharing) {
            setIncomingControlRequest({
              from: data.control_request.from,
              fromName: data.control_request.from_name
            });
          }

          if (data.control_accepted && pendingControlRequest) {
            setIsControllingRemote(true);
            setPendingControlRequest(false);
          }

          if (data.control_rejected && pendingControlRequest) {
            alert('בקשת השליטה נדחתה');
            setPendingControlRequest(false);
          }

          if (data.control_stopped) {
            setRemoteControlActive(false);
            setIsControllingRemote(false);
            setControlRequestFrom(null);
          }
        }
      } catch (err) {
        // Silently fail
      }
    };

    // Poll for screen share updates:
    // - Fast (250ms) when actively sharing/viewing frames
    // - Medium (1000ms) when waiting for approval or already have an incoming request
    // - Slow (5000ms) for passive polling to catch incoming requests from others
    const isActivelySharing = isScreenSharing || activeScreenShare;
    const isWaitingOrHasRequest = pendingScreenShareTo || pendingControlRequest || incomingScreenShareRequest;

    let pollInterval;
    if (isActivelySharing) {
      pollInterval = 500; // 2 checks per second for frame updates (was 250ms)
    } else if (isWaitingOrHasRequest) {
      pollInterval = 2000; // Check every 2 seconds for approval (was 1000ms)
    } else {
      pollInterval = 5000; // Check every 5 seconds for incoming requests (was 3000ms)
    }

    const interval = setInterval(checkScreenShareUpdates, pollInterval);
    return () => clearInterval(interval);
  }, [currentUser?.username, incomingScreenShareRequest, pendingScreenShareTo, activeScreenShare, isScreenSharing, incomingControlRequest, pendingControlRequest]);

  // Execute remote control events (when being controlled)
  useEffect(() => {
    if (!remoteControlActive) return;

    const executeControlEvents = async () => {
      try {
        const data = await apiCall('/api/chat/remote-control/events');
        if (data.success) {
          // Execute mouse events
          for (const event of (data.mouse_events || [])) {
            const screenWidth = window.screen.width;
            const screenHeight = window.screen.height;
            const x = (event.x / 100) * screenWidth;
            const y = (event.y / 100) * screenHeight;

            // Note: Actual mouse control requires system-level access
            // This is a visual simulation - real control needs desktop app
            console.log(`Remote mouse ${event.type} at ${x}, ${y}`);

            // Dispatch simulated events for in-browser control
            if (event.type === 'click' || event.type === 'down' || event.type === 'up') {
              const targetElement = document.elementFromPoint(
                (event.x / 100) * window.innerWidth,
                (event.y / 100) * window.innerHeight
              );
              if (targetElement) {
                if (event.type === 'click') {
                  targetElement.click();
                } else if (event.type === 'down') {
                  targetElement.dispatchEvent(new MouseEvent('mousedown', {
                    bubbles: true,
                    clientX: (event.x / 100) * window.innerWidth,
                    clientY: (event.y / 100) * window.innerHeight
                  }));
                } else if (event.type === 'up') {
                  targetElement.dispatchEvent(new MouseEvent('mouseup', {
                    bubbles: true,
                    clientX: (event.x / 100) * window.innerWidth,
                    clientY: (event.y / 100) * window.innerHeight
                  }));
                }
              }
            }
          }

          // Execute keyboard events
          for (const event of (data.keyboard_events || [])) {
            console.log(`Remote keyboard ${event.type}: ${event.key}`);

            // Dispatch keyboard event to focused element
            const activeElement = document.activeElement || document.body;
            const keyEvent = new KeyboardEvent(event.type, {
              key: event.key,
              keyCode: event.key_code,
              bubbles: true,
              ctrlKey: event.modifiers?.ctrl || false,
              altKey: event.modifiers?.alt || false,
              shiftKey: event.modifiers?.shift || false,
              metaKey: event.modifiers?.meta || false
            });
            activeElement.dispatchEvent(keyEvent);

            // Special handling for input fields
            if (event.type === 'keydown' && activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA') {
              if (event.key.length === 1 && !event.modifiers?.ctrl && !event.modifiers?.alt) {
                // Insert character
                const start = activeElement.selectionStart;
                const end = activeElement.selectionEnd;
                const value = activeElement.value;
                activeElement.value = value.substring(0, start) + event.key + value.substring(end);
                activeElement.selectionStart = activeElement.selectionEnd = start + 1;
                activeElement.dispatchEvent(new Event('input', { bubbles: true }));
              } else if (event.key === 'Backspace') {
                const start = activeElement.selectionStart;
                const end = activeElement.selectionEnd;
                const value = activeElement.value;
                if (start === end && start > 0) {
                  activeElement.value = value.substring(0, start - 1) + value.substring(end);
                  activeElement.selectionStart = activeElement.selectionEnd = start - 1;
                } else if (start !== end) {
                  activeElement.value = value.substring(0, start) + value.substring(end);
                  activeElement.selectionStart = activeElement.selectionEnd = start;
                }
                activeElement.dispatchEvent(new Event('input', { bubbles: true }));
              }
            }
          }
        }
      } catch (err) {
        // Silently fail
      }
    };

    const interval = setInterval(executeControlEvents, 100);
    return () => clearInterval(interval);
  }, [remoteControlActive]);

  // Drag and Drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (view === 'chat') {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.target === chatContainerRef.current) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (view !== 'chat' || !selectedConversation) return;

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const isImage = file.type.startsWith('image/');
        await processFile(file, isImage);
      }
    }
  };

  // Voice recording
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        audioChunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        const reader = new FileReader();
        reader.onload = async () => {
          const voiceData = {
            data: reader.result,
            duration: recordingTime
          };
          await sendMessage('voice', null, voiceData);
        };
        reader.readAsDataURL(audioBlob);

        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);

      recordingIntervalRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    } catch (err) {
      console.error('Error starting recording:', err);
      alert('לא ניתן להפעיל את המיקרופון');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
      }
    }
  };

  const cancelRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
      setIsRecording(false);
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
      }
    }
  };

  // Format recording time
  const formatRecordingTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Select conversation
  const selectConversation = (conv) => {
    setSelectedConversation(conv);
    setView('chat');
  };

  // Start new chat with user
  const startChatWithUser = (user) => {
    setSelectedConversation({
      type: 'private',
      other_user: user.username,
      other_user_name: user.name,
      online_status: user.online_status
    });
    setView('chat');
  };

  // Handle popup click
  const handlePopupClick = () => {
    if (newMessagePopup?.conversation) {
      setSelectedConversation(newMessagePopup.conversation);
      setIsOpen(true);
      setView('chat');
      setNewMessagePopup(null);
    }
  };

  // Scroll to bottom of messages
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Format time
  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' });
  };

  // Format date for message groups
  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) return 'היום';
    if (date.toDateString() === yesterday.toDateString()) return 'אתמול';
    return date.toLocaleDateString('he-IL');
  };

  // Format last seen
  const formatLastSeen = (lastSeen) => {
    if (!lastSeen) return '';
    const date = new Date(lastSeen);
    const now = new Date();
    const diff = now - date;

    if (diff < 60000) return 'נראה לאחרונה עכשיו';
    if (diff < 3600000) return `נראה לאחרונה לפני ${Math.floor(diff / 60000)} דקות`;
    if (diff < 86400000) return `נראה לאחרונה היום ב-${formatTime(lastSeen)}`;
    return `נראה לאחרונה ב-${formatDate(lastSeen)}`;
  };

  // Effects
  useEffect(() => {
    // Don't start polling if no user is logged in
    if (!currentUser?.email && !currentUser?.username) {
      return;
    }

    loadConversations();
    loadUnreadCount();
    updateOnlineStatus();

    pollIntervalRef.current = setInterval(() => {
      checkNewMessages();
      updateOnlineStatus();
    }, 5000);

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [currentUser?.email, currentUser?.username]);

  useEffect(() => {
    if (selectedConversation && view === 'chat') {
      loadMessages();
    }
  }, [selectedConversation]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (view === 'chat' && inputRef.current) {
      inputRef.current.focus();
    }
  }, [view, selectedConversation]);

  useEffect(() => {
    if (view === 'new-chat' || view === 'new-group') {
      // Debounce search queries
      const timeoutId = setTimeout(() => {
        loadUsers(searchQuery);
      }, searchQuery ? 300 : 0); // Immediate for empty query, 300ms delay for search

      return () => clearTimeout(timeoutId);
    }
  }, [view, searchQuery, loadUsers]);

  // Handle key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Handle edit key press
  const handleEditKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (editText.trim() && editingMessage) {
        editMessage(editingMessage.id, editText.trim());
      }
    }
    if (e.key === 'Escape') {
      setEditingMessage(null);
      setEditText('');
    }
  };

  // Group messages by date
  const groupMessagesByDate = (msgs) => {
    const groups = {};
    msgs.forEach(msg => {
      const date = formatDate(msg.timestamp);
      if (!groups[date]) groups[date] = [];
      groups[date].push(msg);
    });
    return groups;
  };

  const messageGroups = groupMessagesByDate(messages);

  // Render message content based on type
  const renderMessageContent = (msg) => {
    if (msg.deleted) {
      return <span style={{ color: '#999', fontStyle: 'italic' }}>{msg.content}</span>;
    }

    if (msg.type === 'system') {
      return (
        <div style={{
          textAlign: 'center',
          padding: '8px 16px',
          background: 'rgba(225, 218, 208, 0.9)',
          borderRadius: '8px',
          fontSize: '12px',
          color: '#54656f',
          margin: '8px auto',
          maxWidth: '80%'
        }}>
          {msg.content}
        </div>
      );
    }

    if (msg.type === 'voice' && msg.voice) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <audio controls src={`${API_BASE}${msg.voice.url}`} style={{ height: '36px' }} />
          <span style={{ fontSize: '12px', color: '#999' }}>
            {formatRecordingTime(msg.voice.duration || 0)}
          </span>
        </div>
      );
    }

    if (msg.type === 'image' && msg.attachment) {
      return (
        <div>
          <img
            src={`${API_BASE}${msg.attachment.url}`}
            alt={msg.attachment.name}
            style={{ maxWidth: '200px', maxHeight: '200px', borderRadius: '8px', cursor: 'pointer' }}
            onClick={() => window.open(`${API_BASE}${msg.attachment.url}`, '_blank')}
          />
          {msg.content && <p style={{ margin: '8px 0 0', fontSize: '14px' }}>{msg.content}</p>}
        </div>
      );
    }

    if (msg.type === 'file' && msg.attachment) {
      return (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          padding: '8px',
          background: 'rgba(0,0,0,0.05)',
          borderRadius: '8px'
        }}>
          <span style={{ fontSize: '24px' }}>📎</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: '500', fontSize: '14px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {msg.attachment.name}
            </div>
            <div style={{ fontSize: '12px', color: '#999' }}>
              {(msg.attachment.size / 1024).toFixed(1)} KB
            </div>
          </div>
          <a
            href={`${API_BASE}${msg.attachment.url}`}
            download={msg.attachment.name}
            style={{
              padding: '6px 12px',
              background: '#25D366',
              color: 'white',
              borderRadius: '4px',
              textDecoration: 'none',
              fontSize: '12px'
            }}
          >
            הורד
          </a>
        </div>
      );
    }

    return (
      <span style={{ fontSize: '14px', color: '#1a1a1a', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        {msg.content}
      </span>
    );
  };

  // Message Action Menu Component
  const MessageActionMenu = ({ msg, isMine, onClose }) => (
    <div style={{
      position: 'absolute',
      top: '100%',
      [isMine ? 'left' : 'right']: '0',
      background: 'white',
      borderRadius: '8px',
      boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
      padding: '4px 0',
      minWidth: '160px',
      zIndex: 1000
    }}>
      <button onClick={() => { setReplyingTo(msg); onClose(); }} style={menuItemStyle}>
        <span>↩️</span> תגובה
      </button>
      <button onClick={() => copyMessage(msg.content)} style={menuItemStyle}>
        <span>📋</span> העתקה
      </button>
      <button onClick={() => forwardMessage(msg)} style={menuItemStyle}>
        <span>↪️</span> העברה
      </button>
      <button onClick={() => starMessage(msg)} style={menuItemStyle}>
        <span>⭐</span> סימון בכוכב
      </button>
      {isMine && !msg.deleted && (
        <>
          <button onClick={() => { setEditingMessage(msg); setEditText(msg.content); onClose(); }} style={menuItemStyle}>
            <span>✏️</span> עריכה
          </button>
          <button onClick={() => deleteMessage(msg.id)} style={{ ...menuItemStyle, color: '#e53935' }}>
            <span>🗑️</span> מחיקה
          </button>
        </>
      )}
    </div>
  );

  const menuItemStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    width: '100%',
    padding: '10px 16px',
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    fontSize: '14px',
    textAlign: 'right',
    transition: 'background 0.2s'
  };

  // Online status indicator
  const OnlineIndicator = ({ status, size = 10 }) => (
    <span style={{
      display: 'inline-block',
      width: size,
      height: size,
      borderRadius: '50%',
      background: status === 'online' ? '#25D366' : '#999',
      border: '2px solid white',
      position: 'absolute',
      bottom: 0,
      right: 0
    }} />
  );

  // User Avatar component with profile picture support
  const UserAvatar = ({ user, size = 45, showOnlineStatus = false }) => {
    const [imgError, setImgError] = useState(false);
    const name = user?.name || user?.username || '?';
    const profilePicture = user?.profilePicture;
    const hasProfilePic = profilePicture && !imgError;

    return (
      <div style={{
        position: 'relative',
        width: size,
        height: size,
        flexShrink: 0
      }}>
        {hasProfilePic ? (
          <img
            src={`${API_BASE}${profilePicture}`}
            alt={name}
            onError={() => setImgError(true)}
            style={{
              width: size,
              height: size,
              borderRadius: '50%',
              objectFit: 'cover'
            }}
          />
        ) : (
          <div style={{
            width: size,
            height: size,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontWeight: 'bold',
            fontSize: size * 0.4
          }}>
            {name[0]?.toUpperCase() || '?'}
          </div>
        )}
        {showOnlineStatus && (
          <OnlineIndicator status={user?.online_status} size={size * 0.25} />
        )}
      </div>
    );
  };

  return (
    <>
      {/* Floating Chat Button Container - Hidden when controlled externally */}
      {!hideFloatingButton && (
      <div style={{
        position: 'fixed',
        bottom: '20px',
        right: '20px',
        zIndex: 99998
      }}>
        {/* Unread Badge - Outside button to float above */}
        {unreadCount > 0 && (
          <span style={{
            position: 'absolute',
            top: '-8px',
            right: '-8px',
            background: '#ff4444',
            color: 'white',
            borderRadius: '12px',
            minWidth: '24px',
            height: '24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '12px',
            fontWeight: 'bold',
            padding: '0 6px',
            boxShadow: '0 2px 8px rgba(255, 68, 68, 0.5)',
            zIndex: 99999,
            border: '2px solid white'
          }}>
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}

        {/* Chat Button */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          style={{
            width: '65px',
            height: '65px',
            borderRadius: '50%',
            background: 'white',
            border: 'none',
            boxShadow: '0 4px 15px rgba(0, 0, 0, 0.25)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'transform 0.2s, box-shadow 0.2s',
            padding: '5px',
            overflow: 'hidden'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'scale(1.1)';
            e.currentTarget.style.boxShadow = '0 6px 20px rgba(0, 0, 0, 0.3)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.boxShadow = '0 4px 15px rgba(0, 0, 0, 0.25)';
          }}
        >
          <img
            src="/assets/afikim_connect_logo.png"
            alt="Afikim Connect"
            style={{
              width: '55px',
              height: '55px',
              objectFit: 'contain',
              borderRadius: '50%'
            }}
            onError={(e) => {
              // Fallback to chat icon if logo fails to load
              e.target.style.display = 'none';
              e.target.parentElement.innerHTML = '<svg width="28" height="28" viewBox="0 0 24 24" fill="#128C7E"><path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z"/></svg>';
            }}
          />
        </button>
      </div>
      )}

      {/* New Message Popup - Bottom notification style */}
      {newMessagePopup && !isOpen && (
        <div
          onClick={handlePopupClick}
          style={{
            position: 'fixed',
            bottom: '20px',
            left: '20px',
            right: '100px',
            background: 'white',
            borderRadius: '12px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
            padding: '16px',
            maxWidth: '400px',
            zIndex: 99997,
            cursor: 'pointer',
            animation: 'slideUp 0.3s ease-out',
            border: '1px solid #e0e0e0',
            borderRight: '4px solid #25D366'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '50px',
              height: '50px',
              borderRadius: '50%',
              background: newMessagePopup.type === 'group'
                ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                : 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontWeight: 'bold',
              fontSize: '20px',
              flexShrink: 0
            }}>
              {newMessagePopup.type === 'group' ? '👥' : (newMessagePopup.sender || '?')[0].toUpperCase()}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ fontWeight: '700', fontSize: '15px', color: '#1a1a1a' }}>
                  {newMessagePopup.type === 'group' ? newMessagePopup.group_name : newMessagePopup.sender}
                </div>
                <div style={{ fontSize: '11px', color: '#999' }}>
                  {formatTime(newMessagePopup.timestamp)}
                </div>
              </div>
              <div style={{
                fontSize: '14px',
                color: '#666',
                marginTop: '4px',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}>
                {newMessagePopup.type === 'group' && `${newMessagePopup.sender}: `}
                {newMessagePopup.content}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Chat Modal - Floating Style */}
      {isOpen && (
        <div
          ref={chatContainerRef}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          style={{
            position: 'fixed',
            bottom: '90px',
            right: '20px',
            width: '380px',
            height: '600px',
            background: '#f0f2f5',
            borderRadius: '20px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3), 0 8px 25px rgba(0,0,0,0.15)',
            zIndex: 9999,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            animation: 'floatIn 0.3s ease-out'
          }}>

          {/* Drag overlay */}
          {isDragging && (
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(37, 211, 102, 0.9)',
              zIndex: 1001,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              borderRadius: '20px'
            }}>
              <span style={{ fontSize: '64px', marginBottom: '16px' }}>📁</span>
              <span style={{ fontSize: '18px', fontWeight: '600' }}>שחרר כאן לשליחה</span>
              <span style={{ fontSize: '14px', marginTop: '8px', opacity: 0.9 }}>תמונות, מסמכים וקבצים</span>
            </div>
          )}

          {/* Header */}
          <div style={{
            background: 'linear-gradient(135deg, #128C7E 0%, #075E54 100%)',
            padding: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            {view !== 'conversations' && (
              <button
                onClick={() => {
                  if (view === 'group-info') {
                    setView('chat');
                  } else {
                    setView('conversations');
                    setSelectedConversation(null);
                    setMessages([]);
                    setSelectedGroupMembers([]);
                    setGroupName('');
                    setGroupDescription('');
                    setReplyingTo(null);
                    setEditingMessage(null);
                  }
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'white',
                  cursor: 'pointer',
                  padding: '4px'
                }}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
                  <path d="M15.41 16.59L10.83 12l4.58-4.59L14 6l-6 6 6 6 1.41-1.41z"/>
                </svg>
              </button>
            )}

            {view === 'chat' && selectedConversation && (
              <>
                <div
                  style={{ position: 'relative', cursor: selectedConversation.type === 'group' ? 'pointer' : 'default' }}
                  onClick={() => selectedConversation.type === 'group' && setView('group-info')}
                >
                  <div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    background: selectedConversation.type === 'group'
                      ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                      : 'linear-gradient(135deg, #128C7E 0%, #075E54 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '18px',
                    overflow: 'hidden'
                  }}>
                    {selectedConversation.type === 'group' ? '👥' : (
                      selectedConversation.other_user_profile_picture ? (
                        <img
                          src={`${API_BASE}${selectedConversation.other_user_profile_picture}`}
                          alt=""
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.parentElement.innerHTML = (selectedConversation.other_user_name || selectedConversation.other_user || '?')[0].toUpperCase();
                          }}
                        />
                      ) : (selectedConversation.other_user_name || selectedConversation.other_user || '?')[0].toUpperCase()
                    )}
                  </div>
                  {selectedConversation.type === 'private' && (
                    <OnlineIndicator status={selectedConversation.online_status} />
                  )}
                </div>
                <div
                  style={{ flex: 1, cursor: selectedConversation.type === 'group' ? 'pointer' : 'default' }}
                  onClick={() => selectedConversation.type === 'group' && setView('group-info')}
                >
                  <div style={{ color: 'white', fontWeight: '600', fontSize: '16px' }}>
                    {selectedConversation.type === 'group'
                      ? selectedConversation.name
                      : (selectedConversation.other_user_name || selectedConversation.other_user)}
                  </div>
                  {selectedConversation.type === 'private' && selectedConversation.other_user_title && (
                    <div style={{ color: 'rgba(255,255,255,0.9)', fontSize: '12px', fontWeight: '500' }}>
                      {selectedConversation.other_user_title}
                    </div>
                  )}
                  <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '11px' }}>
                    {selectedConversation.type === 'group'
                      ? `${selectedConversation.members_count || selectedConversation.members?.length || 0} משתתפים`
                      : (selectedConversation.online_status === 'online' ? 'מקוון' : formatLastSeen(selectedConversation.last_seen))}
                  </div>
                </div>

                {/* Screen Share Button - Only for private chats, shows on all devices */}
                {selectedConversation.type === 'private' && (
                  <button
                    onClick={isScreenSharing ? stopScreenShare : startScreenShare}
                    style={{
                      background: isScreenSharing ? '#ff4444' : 'rgba(255,255,255,0.2)',
                      border: 'none',
                      borderRadius: '50%',
                      width: '36px',
                      height: '36px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      marginRight: '8px',
                      transition: 'all 0.3s ease',
                      opacity: isScreenShareSupported() ? 1 : 0.5
                    }}
                    title={isScreenSharing ? 'הפסק שיתוף מסך' : (isScreenShareSupported() ? 'שתף מסך' : 'שיתוף מסך לא נתמך במכשיר זה')}
                  >
                    {isScreenSharing ? (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="white">
                        <path d="M21 3H3c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H3V5h18v14zM9.41 15.95L12 13.36l2.59 2.59L16 14.54l-2.59-2.59L16 9.36l-1.41-1.41L12 10.54 9.41 7.95 8 9.36l2.59 2.59L8 14.54z"/>
                      </svg>
                    ) : (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="white">
                        <path d="M20 18c1.1 0 1.99-.9 1.99-2L22 6c0-1.1-.9-2-2-2H4c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2H0v2h24v-2h-4zM4 6h16v10H4V6z"/>
                        <path d="M12 8l-4 4h3v3h2v-3h3l-4-4z"/>
                      </svg>
                    )}
                  </button>
                )}
              </>
            )}

            {view === 'conversations' && (
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <img
                  src="/assets/afikim_connect_logo.png"
                  alt="AFIKIM Connect"
                  style={{
                    width: '36px',
                    height: '36px',
                    objectFit: 'contain',
                    borderRadius: '50%'
                  }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
                <div style={{ display: 'flex', gap: '8px' }}>
                  {/* Profile Settings Button */}
                  <button
                    onClick={() => setView('profile-settings')}
                    style={{
                      background: 'rgba(255,255,255,0.2)',
                      border: 'none',
                      borderRadius: '50%',
                      width: '36px',
                      height: '36px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer'
                    }}
                    title="הגדרות פרופיל"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="white">
                      <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                    </svg>
                  </button>
                  {/* Create Group Button - Always visible */}
                  <button
                    onClick={() => {
                      setView('new-group');
                      setSearchQuery('');
                      loadUsers();
                    }}
                    style={{
                      background: 'rgba(255,255,255,0.2)',
                      border: 'none',
                      borderRadius: '50%',
                      width: '36px',
                      height: '36px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer'
                    }}
                    title="קבוצה חדשה"
                  >
                    <span style={{ fontSize: '18px' }}>👥</span>
                  </button>
                  <button
                    onClick={() => {
                      setView('new-chat');
                      setSearchQuery('');
                      loadUsers();
                    }}
                    style={{
                      background: 'rgba(255,255,255,0.2)',
                      border: 'none',
                      borderRadius: '50%',
                      width: '36px',
                      height: '36px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer'
                    }}
                    title="שיחה חדשה"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                      <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
                    </svg>
                  </button>
                </div>
              </div>
            )}

            {view === 'new-chat' && (
              <h3 style={{ color: 'white', margin: 0, fontSize: '18px', fontWeight: '600', flex: 1 }}>
                שיחה חדשה
              </h3>
            )}

            {view === 'new-group' && (
              <h3 style={{ color: 'white', margin: 0, fontSize: '18px', fontWeight: '600', flex: 1 }}>
                קבוצה חדשה
              </h3>
            )}

            {view === 'group-info' && selectedConversation && (
              <h3 style={{ color: 'white', margin: 0, fontSize: '18px', fontWeight: '600', flex: 1 }}>
                פרטי קבוצה
              </h3>
            )}

            {view === 'profile-settings' && (
              <h3 style={{ color: 'white', margin: 0, fontSize: '18px', fontWeight: '600', flex: 1 }}>
                הגדרות פרופיל
              </h3>
            )}

            <button
              onClick={() => setIsOpen(false)}
              style={{
                background: 'none',
                border: 'none',
                color: 'white',
                cursor: 'pointer',
                padding: '4px'
              }}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z"/>
              </svg>
            </button>
          </div>

          {/* Content */}
          <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            {/* Conversations List */}
            {view === 'conversations' && (
              <div style={{ flex: 1, overflowY: 'auto' }}>
                {conversations.length === 0 ? (
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    color: '#999',
                    padding: '20px',
                    textAlign: 'center'
                  }}>
                    <span style={{ fontSize: '64px', marginBottom: '16px' }}>💬</span>
                    <p style={{ margin: 0, fontSize: '16px' }}>אין שיחות עדיין</p>
                    <p style={{ margin: '8px 0 0', fontSize: '14px' }}>לחץ על + כדי להתחיל שיחה חדשה</p>
                  </div>
                ) : (
                  conversations.map((conv) => (
                    <div
                      key={conv.id}
                      onClick={() => selectConversation(conv)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '12px 16px',
                        background: 'white',
                        cursor: 'pointer',
                        borderBottom: '1px solid #f0f0f0',
                        gap: '12px',
                        transition: 'background 0.2s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#f5f5f5'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                    >
                      {conv.type === 'group' ? (
                        <div style={{
                          width: '50px',
                          height: '50px',
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '24px',
                          flexShrink: 0
                        }}>
                          👥
                        </div>
                      ) : (
                        <UserAvatar
                          user={{
                            name: conv.other_user_name || conv.other_user,
                            username: conv.other_user,
                            profilePicture: conv.other_user_profile_picture,
                            online_status: conv.online_status
                          }}
                          size={50}
                          showOnlineStatus={true}
                        />
                      )}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginBottom: '2px'
                        }}>
                          <span style={{
                            fontWeight: conv.unread_count > 0 ? '700' : '500',
                            color: '#1a1a1a',
                            fontSize: '15px'
                          }}>
                            {conv.type === 'group' ? conv.name : (conv.other_user_name || conv.other_user)}
                          </span>
                          <span style={{
                            fontSize: '12px',
                            color: conv.unread_count > 0 ? '#25D366' : '#999'
                          }}>
                            {formatTime(conv.last_message_time)}
                          </span>
                        </div>
                        {conv.type === 'private' && conv.other_user_title && (
                          <div style={{
                            fontSize: '12px',
                            color: '#128C7E',
                            fontWeight: '500',
                            marginBottom: '2px'
                          }}>
                            {conv.other_user_title}
                          </div>
                        )}
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}>
                          <span style={{
                            fontSize: '13px',
                            color: '#666',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            maxWidth: '200px'
                          }}>
                            {conv.type === 'group' && conv.last_sender_name ? `${conv.last_sender_name}: ` : ''}
                            {conv.last_sender === currentUser?.username ? '✓ ' : ''}
                            {conv.last_message}
                          </span>
                          {conv.unread_count > 0 && (
                            <span style={{
                              background: '#25D366',
                              color: 'white',
                              borderRadius: '50%',
                              minWidth: '20px',
                              height: '20px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '12px',
                              fontWeight: 'bold',
                              padding: '0 6px'
                            }}>
                              {conv.unread_count}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* New Chat - User Search */}
            {view === 'new-chat' && (
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
                <div style={{ padding: '12px 16px', background: 'white', borderBottom: '1px solid #e0e0e0', flexShrink: 0 }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    background: '#f0f2f5',
                    borderRadius: '20px',
                    padding: '8px 16px',
                    gap: '8px'
                  }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="#999">
                      <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
                    </svg>
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="חפש משתמש..."
                      style={{
                        flex: 1,
                        border: 'none',
                        background: 'none',
                        outline: 'none',
                        fontSize: '14px',
                        direction: 'rtl'
                      }}
                    />
                  </div>
                </div>
                <div style={{ flex: 1, overflowY: 'auto', background: 'white', minHeight: 0 }}>
                  {users.map((user) => (
                    <div
                      key={user.username}
                      onClick={() => startChatWithUser(user)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '12px 16px',
                        cursor: 'pointer',
                        borderBottom: '1px solid #f0f0f0',
                        gap: '12px',
                        transition: 'background 0.2s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#f5f5f5'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                    >
                      <UserAvatar user={user} size={45} showOnlineStatus={true} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: '500', color: '#1a1a1a', fontSize: '15px' }}>
                          {user.name || user.username}
                        </div>
                        {user.title && (
                          <div style={{ fontSize: '13px', color: '#128C7E', fontWeight: '500' }}>
                            {user.title}
                          </div>
                        )}
                        <div style={{ fontSize: '12px', color: '#999' }}>
                          {user.online_status === 'online' ? 'מקוון' : ''}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* New Group */}
            {view === 'new-group' && (
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                {/* Group info section */}
                <div style={{ padding: '16px', background: 'white', borderBottom: '1px solid #e0e0e0', flexShrink: 0 }}>
                  <input
                    type="text"
                    value={groupName}
                    onChange={(e) => setGroupName(e.target.value)}
                    placeholder="שם הקבוצה *"
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #e0e0e0',
                      borderRadius: '8px',
                      marginBottom: '10px',
                      fontSize: '14px',
                      direction: 'rtl'
                    }}
                  />
                  <input
                    type="text"
                    value={groupDescription}
                    onChange={(e) => setGroupDescription(e.target.value)}
                    placeholder="תיאור הקבוצה (אופציונלי)"
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #e0e0e0',
                      borderRadius: '8px',
                      fontSize: '14px',
                      direction: 'rtl'
                    }}
                  />
                </div>

                {/* Selected members */}
                {selectedGroupMembers.length > 0 && (
                  <div style={{ padding: '12px 16px', background: '#f0f2f5', borderBottom: '1px solid #e0e0e0', maxHeight: '120px', overflowY: 'auto', flexShrink: 0 }}>
                    <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
                      משתתפים נבחרים: {selectedGroupMembers.length}
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      {selectedGroupMembers.map((user) => (
                        <div
                          key={user.username}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            background: 'white',
                            padding: '4px 8px',
                            borderRadius: '16px',
                            fontSize: '13px'
                          }}
                        >
                          <span>{user.name}</span>
                          <button
                            onClick={() => setSelectedGroupMembers(prev =>
                              prev.filter(u => u.username !== user.username)
                            )}
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              color: '#999',
                              padding: '2px'
                            }}
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Search users */}
                <div style={{ padding: '12px 16px', background: 'white', borderBottom: '1px solid #e0e0e0', flexShrink: 0 }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    background: '#f0f2f5',
                    borderRadius: '20px',
                    padding: '8px 16px',
                    gap: '8px'
                  }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="#999">
                      <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
                    </svg>
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="הוסף משתתפים..."
                      style={{
                        flex: 1,
                        border: 'none',
                        background: 'none',
                        outline: 'none',
                        fontSize: '14px',
                        direction: 'rtl'
                      }}
                    />
                  </div>
                </div>

                {/* Users list */}
                <div style={{ flex: 1, overflowY: 'auto', background: 'white' }}>
                  {users.filter(u => !selectedGroupMembers.find(s => s.username === u.username)).map((user) => (
                    <div
                      key={user.username}
                      onClick={() => setSelectedGroupMembers(prev => [...prev, user])}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '12px 16px',
                        cursor: 'pointer',
                        borderBottom: '1px solid #f0f0f0',
                        gap: '12px',
                        transition: 'background 0.2s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#f5f5f5'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                    >
                      <div style={{ position: 'relative' }}>
                        <div style={{
                          width: '45px',
                          height: '45px',
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontWeight: 'bold',
                          fontSize: '18px'
                        }}>
                          {(user.name || user.username || '?')[0].toUpperCase()}
                        </div>
                        <OnlineIndicator status={user.online_status} size={12} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: '500', color: '#1a1a1a', fontSize: '15px' }}>
                          {user.name || user.username}
                        </div>
                        {user.title && (
                          <div style={{ fontSize: '12px', color: '#128C7E', fontWeight: '500' }}>
                            {user.title}
                          </div>
                        )}
                        <div style={{ fontSize: '12px', color: '#999' }}>
                          {user.online_status === 'online' ? 'מקוון' : ''}
                        </div>
                      </div>
                      <div style={{
                        width: '24px',
                        height: '24px',
                        borderRadius: '50%',
                        border: '2px solid #25D366',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="#25D366">
                          <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
                        </svg>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Create button */}
                <div style={{ padding: '16px', background: 'white', borderTop: '1px solid #e0e0e0', flexShrink: 0 }}>
                  <button
                    onClick={createGroup}
                    disabled={!groupName.trim() || selectedGroupMembers.length === 0}
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: groupName.trim() && selectedGroupMembers.length > 0
                        ? 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)'
                        : '#ccc',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '16px',
                      fontWeight: '600',
                      cursor: groupName.trim() && selectedGroupMembers.length > 0 ? 'pointer' : 'default'
                    }}
                  >
                    יצירת קבוצה
                  </button>
                </div>
              </div>
            )}

            {/* Group Info */}
            {view === 'group-info' && selectedConversation?.type === 'group' && (
              <div style={{ flex: 1, overflowY: 'auto', background: 'white' }}>
                <div style={{ padding: '20px', textAlign: 'center', borderBottom: '8px solid #f0f2f5' }}>
                  <div style={{
                    width: '80px',
                    height: '80px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 12px',
                    fontSize: '36px'
                  }}>
                    👥
                  </div>
                  <h3 style={{ margin: '0 0 4px', fontSize: '20px' }}>{selectedConversation.name}</h3>
                  <p style={{ margin: 0, color: '#666', fontSize: '14px' }}>
                    קבוצה • {selectedConversation.members?.length || 0} משתתפים
                  </p>
                  {selectedConversation.description && (
                    <p style={{ margin: '12px 0 0', color: '#333', fontSize: '14px' }}>
                      {selectedConversation.description}
                    </p>
                  )}
                </div>

                <div style={{ padding: '16px' }}>
                  <h4 style={{ margin: '0 0 12px', fontSize: '14px', color: '#128C7E' }}>
                    {selectedConversation.members?.length || 0} משתתפים
                  </h4>
                  {selectedConversation.members?.map((member, idx) => (
                    <div key={idx} style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '12px 0',
                      borderBottom: '1px solid #f0f0f0',
                      gap: '12px'
                    }}>
                      <div style={{
                        width: '45px',
                        height: '45px',
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '18px'
                      }}>
                        {(member || '?')[0].toUpperCase()}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: '500', fontSize: '15px' }}>{member}</div>
                        {selectedConversation.admins?.includes(member) && (
                          <span style={{ fontSize: '12px', color: '#128C7E' }}>מנהל קבוצה</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Profile Settings View */}
            {view === 'profile-settings' && (
              <div style={{ flex: 1, overflowY: 'auto', background: 'white' }}>
                {/* Profile Picture Section */}
                <div style={{ padding: '30px 20px', textAlign: 'center', borderBottom: '8px solid #f0f2f5' }}>
                  <div style={{ position: 'relative', display: 'inline-block' }}>
                    <div style={{
                      width: '120px',
                      height: '120px',
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #128C7E 0%, #075E54 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '48px',
                      fontWeight: 'bold',
                      margin: '0 auto 16px',
                      overflow: 'hidden'
                    }}>
                      {currentUser?.profilePicture ? (
                        <img
                          src={`${API_BASE}${currentUser.profilePicture}`}
                          alt="Profile"
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.parentElement.innerHTML = (currentUser?.name || currentUser?.username || '?')[0].toUpperCase();
                          }}
                        />
                      ) : (
                        (currentUser?.name || currentUser?.username || '?')[0].toUpperCase()
                      )}
                    </div>
                    <button
                      onClick={() => profilePicInputRef.current?.click()}
                      disabled={uploadingProfilePic}
                      style={{
                        position: 'absolute',
                        bottom: '16px',
                        right: '0',
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        background: '#128C7E',
                        border: '3px solid white',
                        color: 'white',
                        cursor: uploadingProfilePic ? 'wait' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      {uploadingProfilePic ? (
                        <div style={{
                          width: '20px',
                          height: '20px',
                          border: '2px solid white',
                          borderTopColor: 'transparent',
                          borderRadius: '50%',
                          animation: 'spin 1s linear infinite'
                        }} />
                      ) : (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                          <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
                        </svg>
                      )}
                    </button>
                    <input
                      ref={profilePicInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleProfilePicUpload}
                      style={{ display: 'none' }}
                    />
                  </div>
                  <div style={{ fontSize: '20px', fontWeight: '600', color: '#1a1a1a' }}>
                    {currentUser?.name || currentUser?.username || 'משתמש'}
                  </div>
                  <div style={{ fontSize: '14px', color: '#666', marginTop: '4px' }}>
                    {currentUser?.email || currentUser?.username}
                  </div>
                </div>

                {/* User Info Section */}
                <div style={{ padding: '20px' }}>
                  <div style={{ fontSize: '14px', color: '#128C7E', fontWeight: '600', marginBottom: '16px' }}>
                    פרטי משתמש
                  </div>
                  <div style={{ background: '#f8f9fa', borderRadius: '12px', padding: '16px' }}>
                    <div style={{ marginBottom: '12px' }}>
                      <div style={{ fontSize: '12px', color: '#999', marginBottom: '4px' }}>שם משתמש</div>
                      <div style={{ fontSize: '15px', color: '#1a1a1a' }}>{currentUser?.username || '-'}</div>
                    </div>
                    {currentUser?.profile && (
                      <div style={{ marginBottom: '12px' }}>
                        <div style={{ fontSize: '12px', color: '#999', marginBottom: '4px' }}>תפקיד</div>
                        <div style={{ fontSize: '15px', color: '#1a1a1a' }}>{currentUser.profile}</div>
                      </div>
                    )}
                    {currentUser?.title && (
                      <div>
                        <div style={{ fontSize: '12px', color: '#999', marginBottom: '4px' }}>כותרת</div>
                        <div style={{ fontSize: '15px', color: '#1a1a1a' }}>{currentUser.title}</div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Help Text */}
                <div style={{ padding: '0 20px 20px', textAlign: 'center' }}>
                  <p style={{ fontSize: '13px', color: '#999', margin: 0 }}>
                    לחץ על סמל העיפרון כדי לשנות את תמונת הפרופיל שלך
                  </p>
                </div>
              </div>
            )}

            {/* Chat View */}
            {view === 'chat' && selectedConversation && (
              <>
                {/* Messages */}
                <div style={{
                  flex: 1,
                  overflowY: 'auto',
                  padding: '16px',
                  background: '#e5ddd5',
                  backgroundImage: 'url("/assets/afikim_connect_logo.png")',
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'center center',
                  backgroundSize: '40%',
                  position: 'relative'
                }}>
                  {loading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                      <div style={{
                        width: '30px',
                        height: '30px',
                        border: '3px solid #e0e0e0',
                        borderTopColor: '#25D366',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                      }} />
                    </div>
                  ) : messages.length === 0 ? (
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '100%',
                      color: '#999'
                    }}>
                      <p>התחל לשוחח {selectedConversation.type === 'group' ? 'בקבוצה' : `עם ${selectedConversation.other_user_name || selectedConversation.other_user}`}</p>
                    </div>
                  ) : (
                    Object.entries(messageGroups).map(([date, msgs]) => (
                      <div key={date}>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'center',
                          marginBottom: '12px'
                        }}>
                          <span style={{
                            background: 'rgba(225, 218, 208, 0.9)',
                            padding: '4px 12px',
                            borderRadius: '8px',
                            fontSize: '12px',
                            color: '#54656f',
                            boxShadow: '0 1px 1px rgba(0,0,0,0.1)'
                          }}>
                            {date}
                          </span>
                        </div>

                        {msgs.map((msg) => {
                          if (msg.type === 'system') {
                            return (
                              <div key={msg.id}>
                                {renderMessageContent(msg)}
                              </div>
                            );
                          }

                          const isMine = msg.sender === currentUser?.username;
                          return (
                            <div
                              key={msg.id}
                              style={{
                                display: 'flex',
                                justifyContent: isMine ? 'flex-end' : 'flex-start',
                                marginBottom: '4px',
                                position: 'relative'
                              }}
                            >
                              <div
                                style={{
                                  maxWidth: '75%',
                                  background: isMine ? '#dcf8c6' : 'white',
                                  borderRadius: isMine ? '8px 0 8px 8px' : '0 8px 8px 8px',
                                  padding: '8px 12px',
                                  boxShadow: '0 1px 1px rgba(0,0,0,0.1)',
                                  position: 'relative'
                                }}
                              >
                                {/* Reply reference */}
                                {msg.reply_to && (
                                  <div style={{
                                    background: 'rgba(0,0,0,0.05)',
                                    borderRight: '3px solid #25D366',
                                    padding: '4px 8px',
                                    marginBottom: '6px',
                                    borderRadius: '4px',
                                    fontSize: '12px',
                                    color: '#666'
                                  }}>
                                    {msg.reply_to_content || 'הודעה'}
                                  </div>
                                )}

                                {/* Message menu arrow */}
                                <button
                                  onClick={() => setMessageMenuOpen(messageMenuOpen === msg.id ? null : msg.id)}
                                  style={{
                                    position: 'absolute',
                                    top: '4px',
                                    [isMine ? 'left' : 'right']: '4px',
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    padding: '2px',
                                    opacity: 0.5,
                                    transition: 'opacity 0.2s'
                                  }}
                                  onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                                  onMouseLeave={(e) => e.currentTarget.style.opacity = '0.5'}
                                >
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="#666">
                                    <path d="M7 10l5 5 5-5z"/>
                                  </svg>
                                </button>

                                {/* Message action menu */}
                                {messageMenuOpen === msg.id && (
                                  <MessageActionMenu
                                    msg={msg}
                                    isMine={isMine}
                                    onClose={() => setMessageMenuOpen(null)}
                                  />
                                )}

                                {/* Sender name for group messages */}
                                {selectedConversation.type === 'group' && !isMine && (
                                  <div style={{
                                    fontSize: '12px',
                                    fontWeight: '600',
                                    color: '#128C7E',
                                    marginBottom: '4px'
                                  }}>
                                    {msg.sender_name || msg.sender}
                                  </div>
                                )}

                                {/* Edit mode */}
                                {editingMessage?.id === msg.id ? (
                                  <div>
                                    <input
                                      type="text"
                                      value={editText}
                                      onChange={(e) => setEditText(e.target.value)}
                                      onKeyDown={handleEditKeyPress}
                                      autoFocus
                                      style={{
                                        width: '100%',
                                        padding: '8px',
                                        border: '1px solid #25D366',
                                        borderRadius: '4px',
                                        fontSize: '14px',
                                        direction: 'rtl'
                                      }}
                                    />
                                    <div style={{ display: 'flex', gap: '8px', marginTop: '8px', justifyContent: 'flex-end' }}>
                                      <button
                                        onClick={() => { setEditingMessage(null); setEditText(''); }}
                                        style={{
                                          padding: '4px 12px',
                                          border: '1px solid #ccc',
                                          borderRadius: '4px',
                                          background: 'white',
                                          cursor: 'pointer',
                                          fontSize: '12px'
                                        }}
                                      >
                                        ביטול
                                      </button>
                                      <button
                                        onClick={() => editMessage(msg.id, editText)}
                                        style={{
                                          padding: '4px 12px',
                                          border: 'none',
                                          borderRadius: '4px',
                                          background: '#25D366',
                                          color: 'white',
                                          cursor: 'pointer',
                                          fontSize: '12px'
                                        }}
                                      >
                                        שמור
                                      </button>
                                    </div>
                                  </div>
                                ) : (
                                  renderMessageContent(msg)
                                )}

                                <div style={{
                                  display: 'flex',
                                  justifyContent: 'flex-end',
                                  alignItems: 'center',
                                  gap: '4px',
                                  marginTop: '4px'
                                }}>
                                  {msg.edited && (
                                    <span style={{ fontSize: '11px', color: '#999' }}>נערך</span>
                                  )}
                                  <span style={{ fontSize: '11px', color: '#999' }}>
                                    {formatTime(msg.timestamp)}
                                  </span>
                                  {isMine && (
                                    <span style={{ color: msg.read ? '#53bdeb' : '#999', fontSize: '14px' }}>
                                      {msg.read ? '✓✓' : '✓'}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ))
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {/* Reply indicator */}
                {replyingTo && (
                  <div style={{
                    padding: '8px 16px',
                    background: '#f0f2f5',
                    borderTop: '1px solid #e0e0e0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div style={{ width: '4px', height: '40px', background: '#25D366', borderRadius: '2px' }} />
                      <div>
                        <div style={{ fontSize: '12px', fontWeight: '600', color: '#25D366' }}>
                          {replyingTo.sender_name || replyingTo.sender}
                        </div>
                        <div style={{ fontSize: '13px', color: '#666', maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {replyingTo.content}
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => setReplyingTo(null)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}
                    >
                      ✕
                    </button>
                  </div>
                )}

                {/* Message Input */}
                <div style={{
                  padding: '10px 16px',
                  background: '#f0f2f5',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}>
                  {isRecording ? (
                    // Recording UI
                    <div style={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      background: 'white',
                      borderRadius: '24px',
                      padding: '8px 16px'
                    }}>
                      <button
                        onClick={cancelRecording}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          color: '#f44336'
                        }}
                      >
                        ✕
                      </button>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{
                          width: '10px',
                          height: '10px',
                          borderRadius: '50%',
                          background: '#f44336',
                          animation: 'pulse 1s infinite'
                        }} />
                        <span>{formatRecordingTime(recordingTime)}</span>
                      </div>
                      <button
                        onClick={stopRecording}
                        style={{
                          background: '#25D366',
                          border: 'none',
                          borderRadius: '50%',
                          width: '36px',
                          height: '36px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer'
                        }}
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="white">
                          <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
                        </svg>
                      </button>
                    </div>
                  ) : (
                    <>
                      {/* Attach button */}
                      <div style={{ position: 'relative' }}>
                        <button
                          onClick={() => setShowAttachMenu(!showAttachMenu)}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="#54656f">
                            <path d="M16.5 6v11.5c0 2.21-1.79 4-4 4s-4-1.79-4-4V5c0-1.38 1.12-2.5 2.5-2.5s2.5 1.12 2.5 2.5v10.5c0 .55-.45 1-1 1s-1-.45-1-1V6H10v9.5c0 1.38 1.12 2.5 2.5 2.5s2.5-1.12 2.5-2.5V5c0-2.21-1.79-4-4-4S7 2.79 7 5v12.5c0 3.04 2.46 5.5 5.5 5.5s5.5-2.46 5.5-5.5V6h-1.5z"/>
                          </svg>
                        </button>

                        {showAttachMenu && (
                          <div style={{
                            position: 'absolute',
                            bottom: '50px',
                            right: 0,
                            background: 'white',
                            borderRadius: '12px',
                            boxShadow: '0 2px 10px rgba(0,0,0,0.15)',
                            padding: '8px 0',
                            minWidth: '150px',
                            zIndex: 100
                          }}>
                            <button
                              onClick={() => imageInputRef.current?.click()}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                width: '100%',
                                padding: '10px 16px',
                                border: 'none',
                                background: 'none',
                                cursor: 'pointer',
                                fontSize: '14px'
                              }}
                            >
                              <span style={{ fontSize: '20px' }}>📷</span>
                              תמונה
                            </button>
                            <button
                              onClick={() => fileInputRef.current?.click()}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                width: '100%',
                                padding: '10px 16px',
                                border: 'none',
                                background: 'none',
                                cursor: 'pointer',
                                fontSize: '14px'
                              }}
                            >
                              <span style={{ fontSize: '20px' }}>📎</span>
                              קובץ
                            </button>
                          </div>
                        )}
                      </div>

                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={(e) => handleFileSelect(e, false)}
                        style={{ display: 'none' }}
                      />
                      <input
                        type="file"
                        ref={imageInputRef}
                        accept="image/*"
                        onChange={(e) => handleFileSelect(e, true)}
                        style={{ display: 'none' }}
                      />

                      {/* Text input */}
                      <div style={{
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        background: 'white',
                        borderRadius: '24px',
                        padding: '8px 16px'
                      }}>
                        <input
                          ref={inputRef}
                          type="text"
                          value={newMessage}
                          onChange={(e) => setNewMessage(e.target.value)}
                          onKeyPress={handleKeyPress}
                          placeholder="הקלד הודעה..."
                          style={{
                            flex: 1,
                            border: 'none',
                            outline: 'none',
                            fontSize: '15px',
                            direction: 'rtl'
                          }}
                        />
                      </div>

                      {/* Voice/Send button */}
                      {newMessage.trim() ? (
                        <button
                          onClick={() => sendMessage()}
                          style={{
                            width: '45px',
                            height: '45px',
                            borderRadius: '50%',
                            background: '#25D366',
                            border: 'none',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer'
                          }}
                        >
                          <svg width="22" height="22" viewBox="0 0 24 24" fill="white">
                            <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
                          </svg>
                        </button>
                      ) : (
                        <button
                          onClick={startRecording}
                          style={{
                            width: '45px',
                            height: '45px',
                            borderRadius: '50%',
                            background: '#25D366',
                            border: 'none',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer'
                          }}
                        >
                          <svg width="22" height="22" viewBox="0 0 24 24" fill="white">
                            <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5.91-3c-.49 0-.9.36-.98.85C16.52 14.2 14.47 16 12 16s-4.52-1.8-4.93-4.15c-.08-.49-.49-.85-.98-.85-.61 0-1.09.54-1 1.14.49 3 2.89 5.35 5.91 5.78V20c0 .55.45 1 1 1s1-.45 1-1v-2.08c3.02-.43 5.42-2.78 5.91-5.78.1-.6-.39-1.14-1-1.14z"/>
                          </svg>
                        </button>
                      )}
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Click outside to close menus */}
      {(showAttachMenu || messageMenuOpen) && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 9998
          }}
          onClick={() => {
            setShowAttachMenu(false);
            setMessageMenuOpen(null);
          }}
        />
      )}

      {/* Incoming Screen Share Request Modal */}
      {incomingScreenShareRequest && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 100000,
          animation: 'fadeIn 0.3s ease-out'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '20px',
            padding: '30px',
            maxWidth: '400px',
            width: '90%',
            textAlign: 'center',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
            animation: 'slideUp 0.3s ease-out'
          }}>
            {/* Icon */}
            <div style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px',
              boxShadow: '0 8px 25px rgba(102, 126, 234, 0.4)'
            }}>
              <svg width="40" height="40" viewBox="0 0 24 24" fill="white">
                <path d="M20 18c1.1 0 1.99-.9 1.99-2L22 6c0-1.1-.9-2-2-2H4c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2H0v2h24v-2h-4zM4 6h16v10H4V6z"/>
              </svg>
            </div>

            {/* Title */}
            <h2 style={{
              margin: '0 0 10px',
              fontSize: '22px',
              fontWeight: '700',
              color: '#1a1a1a'
            }}>
              בקשה לשיתוף מסך
            </h2>

            {/* Description */}
            <p style={{
              margin: '0 0 25px',
              fontSize: '16px',
              color: '#666',
              lineHeight: '1.5'
            }}>
              <strong style={{ color: '#128C7E' }}>{incomingScreenShareRequest.fromName}</strong>
              <br />
              מבקש/ת לשתף איתך את המסך שלו/ה
            </p>

            {/* Info box */}
            <div style={{
              background: '#f8f9fa',
              borderRadius: '12px',
              padding: '12px 16px',
              marginBottom: '25px',
              fontSize: '13px',
              color: '#666',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="#128C7E">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
              </svg>
              תוכל/י לראות את המסך שלו/ה בזמן אמת
            </div>

            {/* Buttons */}
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button
                onClick={rejectScreenShare}
                style={{
                  flex: 1,
                  padding: '14px 24px',
                  border: '2px solid #e0e0e0',
                  borderRadius: '12px',
                  background: 'white',
                  color: '#666',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.borderColor = '#ff4444';
                  e.target.style.color = '#ff4444';
                }}
                onMouseLeave={(e) => {
                  e.target.style.borderColor = '#e0e0e0';
                  e.target.style.color = '#666';
                }}
              >
                סירוב
              </button>
              <button
                onClick={acceptScreenShare}
                style={{
                  flex: 1,
                  padding: '14px 24px',
                  border: 'none',
                  borderRadius: '12px',
                  background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)',
                  color: 'white',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  boxShadow: '0 4px 15px rgba(37, 211, 102, 0.3)',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 6px 20px rgba(37, 211, 102, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 15px rgba(37, 211, 102, 0.3)';
                }}
              >
                אישור
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Screen Share Viewer */}
      {activeScreenShare && (
        <div
          ref={screenShareViewerRef}
          tabIndex={isControllingRemote ? 0 : -1}
          onMouseMove={(e) => handleViewerMouseEvent(e, 'move')}
          onMouseDown={(e) => handleViewerMouseEvent(e, 'down')}
          onMouseUp={(e) => handleViewerMouseEvent(e, 'up')}
          onClick={(e) => handleViewerMouseEvent(e, 'click')}
          onDoubleClick={(e) => handleViewerMouseEvent(e, 'dblclick')}
          onContextMenu={(e) => { e.preventDefault(); handleViewerMouseEvent(e, 'contextmenu'); }}
          onKeyDown={(e) => handleViewerKeyEvent(e, 'keydown')}
          onKeyUp={(e) => handleViewerKeyEvent(e, 'keyup')}
          style={{
            position: 'fixed',
            ...(isScreenShareFullscreen
              ? { top: 0, left: 0, right: 0, bottom: 0, borderRadius: 0 }
              : { bottom: '100px', right: '20px', width: '400px', height: '250px', borderRadius: '16px' }
            ),
            background: '#000',
            boxShadow: isScreenShareFullscreen ? 'none' : '0 10px 40px rgba(0, 0, 0, 0.4)',
            zIndex: 100001,
            overflow: 'hidden',
            transition: 'all 0.3s ease',
            animation: 'fadeIn 0.3s ease-out',
            cursor: isControllingRemote ? 'none' : 'default',
            outline: isControllingRemote ? '3px solid #25D366' : 'none'
          }}
        >
          {/* Header */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            padding: '12px 16px',
            background: 'linear-gradient(to bottom, rgba(0,0,0,0.8), transparent)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            zIndex: 10
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{
                width: '10px',
                height: '10px',
                borderRadius: '50%',
                background: isControllingRemote ? '#25D366' : '#ff4444',
                animation: 'pulse 1s infinite'
              }} />
              <span style={{ color: 'white', fontSize: '14px', fontWeight: '600' }}>
                {isControllingRemote ? 'שולט במסך של ' : 'שיתוף מסך מ-'}{activeScreenShare.fromName}
              </span>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              {/* Request/Stop Control Button */}
              {!isControllingRemote && !pendingControlRequest && (
                <button
                  onClick={requestRemoteControl}
                  style={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '6px 12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    color: 'white',
                    fontSize: '12px',
                    fontWeight: '600'
                  }}
                  onMouseEnter={(e) => e.target.style.transform = 'scale(1.05)'}
                  onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                  title="בקש שליטה במסך"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="white">
                    <path d="M13 8c0-2.21-1.79-4-4-4S5 5.79 5 8s1.79 4 4 4 4-1.79 4-4zm-2 0c0 1.1-.9 2-2 2s-2-.9-2-2 .9-2 2-2 2 .9 2 2zM1 18v2h16v-2c0-2.66-5.33-4-8-4s-8 1.34-8 4zm2 0c.2-.71 3.3-2 6-2s5.8 1.29 6 2H3zM20 15v-3h3v-2h-3V7h-2v3h-3v2h3v3h2z"/>
                  </svg>
                  בקש שליטה
                </button>
              )}
              {pendingControlRequest && (
                <div style={{
                  background: 'rgba(255,255,255,0.2)',
                  borderRadius: '8px',
                  padding: '6px 12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  color: 'white',
                  fontSize: '12px'
                }}>
                  <div style={{
                    width: '12px',
                    height: '12px',
                    border: '2px solid white',
                    borderTopColor: 'transparent',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }} />
                  ממתין לאישור...
                </div>
              )}
              {isControllingRemote && (
                <button
                  onClick={stopRemoteControl}
                  style={{
                    background: 'linear-gradient(135deg, #ff4444 0%, #cc0000 100%)',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '6px 12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    cursor: 'pointer',
                    color: 'white',
                    fontSize: '12px',
                    fontWeight: '600'
                  }}
                  title="הפסק שליטה"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="white">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm5 11H7v-2h10v2z"/>
                  </svg>
                  הפסק שליטה
                </button>
              )}
              {/* Fullscreen toggle */}
              <button
                onClick={toggleScreenShareFullscreen}
                style={{
                  background: 'rgba(255,255,255,0.2)',
                  border: 'none',
                  borderRadius: '8px',
                  width: '36px',
                  height: '36px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  transition: 'background 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.background = 'rgba(255,255,255,0.3)'}
                onMouseLeave={(e) => e.target.style.background = 'rgba(255,255,255,0.2)'}
                title={isScreenShareFullscreen ? 'צמצם' : 'מסך מלא'}
              >
                {isScreenShareFullscreen ? (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="white">
                    <path d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z"/>
                  </svg>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="white">
                    <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"/>
                  </svg>
                )}
              </button>
              {/* Close button */}
              <button
                onClick={closeScreenShareViewer}
                style={{
                  background: 'rgba(255, 68, 68, 0.8)',
                  border: 'none',
                  borderRadius: '8px',
                  width: '36px',
                  height: '36px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  transition: 'background 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.background = '#ff4444'}
                onMouseLeave={(e) => e.target.style.background = 'rgba(255, 68, 68, 0.8)'}
                title="סגור"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="white">
                  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z"/>
                </svg>
              </button>
            </div>
          </div>

          {/* Screen Content */}
          <div style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: '#1a1a1a'
          }}>
            {screenShareFrame ? (
              <img
                src={screenShareFrame}
                alt="Shared Screen"
                style={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain',
                  pointerEvents: isControllingRemote ? 'none' : 'auto'
                }}
                draggable={false}
              />
            ) : (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '16px',
                color: '#666'
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  border: '3px solid #333',
                  borderTopColor: '#25D366',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }} />
                <span>ממתין לשיתוף מסך...</span>
              </div>
            )}
          </div>

          {/* Control mode indicator */}
          {isControllingRemote && (
            <div style={{
              position: 'absolute',
              bottom: '16px',
              left: '50%',
              transform: 'translateX(-50%)',
              background: 'rgba(37, 211, 102, 0.9)',
              color: 'white',
              padding: '8px 16px',
              borderRadius: '20px',
              fontSize: '12px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              zIndex: 20
            }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="white">
                <path d="M13 8c0-2.21-1.79-4-4-4S5 5.79 5 8s1.79 4 4 4 4-1.79 4-4zm-2 0c0 1.1-.9 2-2 2s-2-.9-2-2 .9-2 2-2 2 .9 2 2z"/>
              </svg>
              מצב שליטה פעיל - השתמש בעכבר ומקלדת
            </div>
          )}

          {/* Mouse cursor indicator (if position is available) */}
          {screenShareFrame && mousePosition && mousePosition.x > 0 && (
            <div style={{
              position: 'absolute',
              left: `${mousePosition.x}%`,
              top: `${mousePosition.y}%`,
              width: '20px',
              height: '20px',
              background: 'rgba(255, 68, 68, 0.6)',
              borderRadius: '50%',
              transform: 'translate(-50%, -50%)',
              pointerEvents: 'none',
              boxShadow: '0 0 10px rgba(255, 68, 68, 0.8)',
              transition: 'left 0.05s, top 0.05s'
            }} />
          )}

          {/* Resize handle for non-fullscreen mode */}
          {!isScreenShareFullscreen && (
            <div style={{
              position: 'absolute',
              bottom: '4px',
              right: '4px',
              width: '20px',
              height: '20px',
              cursor: 'nwse-resize',
              opacity: 0.5
            }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                <path d="M22 22H20V20H22V22ZM22 18H20V16H22V18ZM18 22H16V20H18V22ZM18 18H16V16H18V18ZM14 22H12V20H14V22ZM22 14H20V12H22V14Z"/>
              </svg>
            </div>
          )}
        </div>
      )}

      {/* Screen Sharing Active Indicator (for sharer) */}
      {isScreenSharing && (
        <div style={{
          position: 'fixed',
          top: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          background: remoteControlActive
            ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
            : 'linear-gradient(135deg, #ff4444 0%, #cc0000 100%)',
          color: 'white',
          padding: '12px 24px',
          borderRadius: '30px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          boxShadow: remoteControlActive
            ? '0 4px 20px rgba(102, 126, 234, 0.4)'
            : '0 4px 20px rgba(255, 68, 68, 0.4)',
          zIndex: 100002,
          animation: 'slideUp 0.3s ease-out'
        }}>
          <div style={{
            width: '12px',
            height: '12px',
            borderRadius: '50%',
            background: 'white',
            animation: 'pulse 1s infinite'
          }} />
          <span style={{ fontWeight: '600', fontSize: '14px' }}>
            {remoteControlActive ? 'נשלט מרחוק...' : 'משתף מסך...'}
          </span>
          {remoteControlActive && (
            <button
              onClick={stopRemoteControl}
              style={{
                background: 'rgba(255,255,255,0.2)',
                border: 'none',
                borderRadius: '20px',
                padding: '6px 16px',
                color: 'white',
                fontSize: '13px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'background 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.background = 'rgba(255,255,255,0.3)'}
              onMouseLeave={(e) => e.target.style.background = 'rgba(255,255,255,0.2)'}
            >
              הפסק שליטה
            </button>
          )}
          <button
            onClick={stopScreenShare}
            style={{
              background: 'rgba(255,255,255,0.2)',
              border: 'none',
              borderRadius: '20px',
              padding: '6px 16px',
              color: 'white',
              fontSize: '13px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'background 0.2s'
            }}
            onMouseEnter={(e) => e.target.style.background = 'rgba(255,255,255,0.3)'}
            onMouseLeave={(e) => e.target.style.background = 'rgba(255,255,255,0.2)'}
          >
            הפסק שיתוף
          </button>
        </div>
      )}

      {/* Incoming Remote Control Request Modal */}
      {incomingControlRequest && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 100003,
          animation: 'fadeIn 0.3s ease-out'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '20px',
            padding: '30px',
            maxWidth: '420px',
            width: '90%',
            textAlign: 'center',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
            animation: 'slideUp 0.3s ease-out'
          }}>
            {/* Icon */}
            <div style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px',
              boxShadow: '0 8px 25px rgba(255, 152, 0, 0.4)'
            }}>
              <svg width="40" height="40" viewBox="0 0 24 24" fill="white">
                <path d="M13 8c0-2.21-1.79-4-4-4S5 5.79 5 8s1.79 4 4 4 4-1.79 4-4zm-2 0c0 1.1-.9 2-2 2s-2-.9-2-2 .9-2 2-2 2 .9 2 2zM1 18v2h16v-2c0-2.66-5.33-4-8-4s-8 1.34-8 4zm2 0c.2-.71 3.3-2 6-2s5.8 1.29 6 2H3z"/>
                <path d="M20 10V7h-2v3h-3v2h3v3h2v-3h3v-2h-3z"/>
              </svg>
            </div>

            {/* Title */}
            <h2 style={{
              margin: '0 0 10px',
              fontSize: '22px',
              fontWeight: '700',
              color: '#1a1a1a'
            }}>
              בקשה לשליטה מרחוק
            </h2>

            {/* Description */}
            <p style={{
              margin: '0 0 20px',
              fontSize: '16px',
              color: '#666',
              lineHeight: '1.5'
            }}>
              <strong style={{ color: '#f57c00' }}>{incomingControlRequest.fromName}</strong>
              <br />
              מבקש/ת לשלוט במחשב שלך
            </p>

            {/* Warning box */}
            <div style={{
              background: '#fff3e0',
              border: '1px solid #ffcc80',
              borderRadius: '12px',
              padding: '12px 16px',
              marginBottom: '25px',
              fontSize: '13px',
              color: '#e65100',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              textAlign: 'right'
            }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="#e65100" style={{ flexShrink: 0 }}>
                <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/>
              </svg>
              <span>
                המשתמש יוכל לשלוט בעכבר ובמקלדת שלך.
                אשר רק אם אתה סומך על המבקש!
              </span>
            </div>

            {/* Buttons */}
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button
                onClick={rejectRemoteControl}
                style={{
                  flex: 1,
                  padding: '14px 24px',
                  border: '2px solid #e0e0e0',
                  borderRadius: '12px',
                  background: 'white',
                  color: '#666',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.borderColor = '#ff4444';
                  e.target.style.color = '#ff4444';
                }}
                onMouseLeave={(e) => {
                  e.target.style.borderColor = '#e0e0e0';
                  e.target.style.color = '#666';
                }}
              >
                סירוב
              </button>
              <button
                onClick={acceptRemoteControl}
                style={{
                  flex: 1,
                  padding: '14px 24px',
                  border: 'none',
                  borderRadius: '12px',
                  background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
                  color: 'white',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  boxShadow: '0 4px 15px rgba(255, 152, 0, 0.3)',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 6px 20px rgba(255, 152, 0, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 15px rgba(255, 152, 0, 0.3)';
                }}
              >
                אישור שליטה
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Animations */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: scale(0.9); }
          to { opacity: 1; transform: scale(1); }
        }
        @keyframes floatIn {
          from {
            opacity: 0;
            transform: translateY(30px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        @keyframes slideIn {
          from { opacity: 0; transform: translateX(20px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
    </>
  );
};

export default InternalChat;
